import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ExternalLink, Monitor, Smartphone } from "lucide-react";
import { getCategoryIcon } from "@/lib/categoryIcons";
import DOMPurify from "dompurify";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageSEO } from "@/hooks/usePageSEO";
import { resolveAssetUrl } from "@/lib/media";

interface WorkData {
  id: string;
  title: string;
  title_ar: string | null;
  slug: string;
  industry: string | null;
  services_provided: string[] | null;
  short_description: string | null;
  short_description_ar: string | null;
  content: string;
  content_ar: string | null;
  featured_image: string | null;
  gallery_images: string[] | null;
  website_url: string | null;
  tags: string[] | null;
  seo_title: string | null;
  seo_description: string | null;
  desktop_screenshot: string | null;
  mobile_screenshot: string | null;
}

interface RelatedWork {
  id: string;
  title: string;
  title_ar: string | null;
  slug: string;
  featured_image: string | null;
  industry: string | null;
}

export default function WorkDetail() {
  const { slug } = useParams();
  const [work, setWork] = useState<WorkData | null>(null);
  const [related, setRelated] = useState<RelatedWork[]>([]);
  const [loading, setLoading] = useState(true);
  const { t, localePath } = useLanguage();

  useEffect(() => {
    const fetchWork = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("work")
        .select("*")
        .eq("slug", slug)
        .single();

      setWork(data);

      if (data?.industry) {
        const { data: rel } = await supabase
          .from("work")
          .select("id, title, title_ar, slug, featured_image, industry")
          .eq("industry", data.industry)
          .neq("id", data.id)
          .limit(4);
        setRelated(rel || []);
      }
      setLoading(false);
    };
    if (slug) fetchWork();
  }, [slug]);

  const seoData = useMemo(() => {
    if (!work) return null;
    const displayTitle = t(work.title, work.title_ar);
    const pageTitle = work.seo_title || displayTitle + " | Wavai";
    const pageDesc = work.seo_description || t(work.short_description || "", work.short_description_ar) || "";
    const pageImage = resolveAssetUrl(work.featured_image);
    const base = "https://www.wavai.ae";
    return {
      title: pageTitle,
      description: pageDesc,
      ogImage: pageImage,
      ogType: "article" as const,
      canonical: `${base}/work/${work.slug}`,
      hreflang: [
        { lang: "en", href: `${base}/work/${work.slug}` },
        { lang: "ar", href: `${base}/ar/work/${work.slug}` },
        { lang: "x-default", href: `${base}/work/${work.slug}` },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "CreativeWork",
        name: displayTitle,
        description: pageDesc,
        image: pageImage || undefined,
        url: `${base}/work/${work.slug}`,
        creator: { "@type": "Organization", name: "Wavai", url: "https://www.wavai.ae" },
      },
    };
  }, [work, t]);

  usePageSEO(seoData || {
    title: "Work | Wavai",
    description: "View our portfolio.",
  });

  if (loading) return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-muted-foreground">{t("Loading...", "جاري التحميل...")}</p>
      </div>
      <Footer />
    </div>
  );

  if (!work) return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <h1 className="text-2xl font-bold">{t("Project not found", "المشروع غير موجود")}</h1>
        <Button asChild><Link to={localePath("/work")}>{t("Back to Work", "العودة للأعمال")}</Link></Button>
      </div>
      <Footer />
    </div>
  );

  const displayTitle = t(work.title, work.title_ar);
  const displayDescription = t(work.short_description || "", work.short_description_ar);
  const displayContent = t(work.content, work.content_ar);
  const featuredImageSrc = resolveAssetUrl(work.featured_image);
  const galleryImages = work.gallery_images?.map((img) => resolveAssetUrl(img)) || [];
  const desktopScreenshotSrc = resolveAssetUrl(work.desktop_screenshot);
  const mobileScreenshotSrc = resolveAssetUrl(work.mobile_screenshot);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 relative">
          <div className="absolute inset-0 bg-gradient-hero opacity-40" />
          <div className="container px-6 relative z-10">
            <Link to={localePath("/work")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">
              <ArrowLeft className="h-4 w-4" /> {t("Back to Work", "العودة للأعمال")}
            </Link>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {work.industry && (() => {
                    const IconComp = getCategoryIcon(work.industry);
                    return (
                      <Badge variant="secondary" className="inline-flex items-center gap-1.5">
                        <IconComp size={14} weight="duotone" />
                        {work.industry}
                      </Badge>
                    );
                  })()}
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-6">{displayTitle}</h1>
                {displayDescription && (
                  <p className="text-lg text-muted-foreground mb-8">{displayDescription}</p>
                )}
                {work.services_provided && work.services_provided.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-8">
                    {work.services_provided.map((s) => (
                      <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                    ))}
                  </div>
                )}
                <div className="flex gap-3 items-stretch">
                  <Button asChild variant="hero" size="lg" className="flex-1 sm:flex-none text-sm sm:text-base px-4 sm:px-6">
                    <Link to={localePath("/start-a-project")}>{t("Start a Project", "ابدأ مشروعك")}</Link>
                  </Button>
                  {work.website_url && (
                    <Button asChild variant="outline" size="lg" className="flex-1 sm:flex-none text-sm sm:text-base px-4 sm:px-6">
                      <a href={work.website_url} target="_blank" rel="noopener noreferrer" className="gap-2">
                        {t("Visit Website", "زيارة الموقع")} <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
              {work.featured_image && (
                <div className="rounded-2xl overflow-hidden border border-border shadow-card">
                  <img src={featuredImageSrc} alt={displayTitle} className="w-full h-auto" loading="lazy" />
                </div>
              )}
            </div>
          </div>
        </section>

        {displayContent && displayContent.trim() !== "" && (
          <section className="py-12 lg:py-32">
            <div className="container px-6">
              <div className="max-w-3xl mx-auto prose prose-lg max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(displayContent) }} />
            </div>
          </section>
        )}

        {galleryImages.length > 0 && (
          <section className="py-12 lg:py-32 bg-secondary/30">
            <div className="container px-6">
              <h2 className="text-2xl font-display font-bold mb-8">{t("Project Gallery", "معرض المشروع")}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {galleryImages.map((img, i) => (
                  <div key={i} className="rounded-2xl overflow-hidden border border-border">
                    <img src={img} alt={`${displayTitle} ${t("screenshot", "لقطة شاشة")} ${i + 1}`} className="w-full h-auto" loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {(work.desktop_screenshot || work.mobile_screenshot) && (
          <section className="py-12 lg:py-32 bg-secondary/30">
            <div className="container px-6">
              <h2 className="text-2xl font-display font-bold mb-8">{t("Website Preview", "معاينة الموقع")}</h2>
              <div className="grid lg:grid-cols-3 gap-8 items-start">
                {work.desktop_screenshot && (
                  <div className="lg:col-span-2 space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Monitor className="h-4 w-4" /> {t("Desktop View", "عرض سطح المكتب")}
                    </div>
                    <div className="rounded-2xl overflow-hidden border border-border shadow-card bg-card">
                      <img src={desktopScreenshotSrc} alt={`${displayTitle}, Desktop`} className="w-full h-auto" loading="lazy" />
                    </div>
                  </div>
                )}
                {work.mobile_screenshot && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Smartphone className="h-4 w-4" /> {t("Mobile View", "عرض الجوال")}
                    </div>
                    <div className="rounded-2xl overflow-hidden border border-border shadow-card bg-card max-w-[280px]">
                      <img src={mobileScreenshotSrc} alt={`${displayTitle}, Mobile`} className="w-full h-auto" loading="lazy" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        <section className="py-12 lg:py-32">
          <div className="container px-6">
            <div className="max-w-3xl mx-auto text-center p-8 rounded-xl bg-gradient-hero border border-border">
              <h2 className="text-2xl font-display font-bold mb-4">{t("Ready to start your project?", "هل أنت مستعد لبدء مشروعك؟")}</h2>
              <p className="text-muted-foreground mb-6">{t("Let Wavai build, launch, and scale your digital presence.", "دع وافاي تبني وتطلق وتوسع حضورك الرقمي.")}</p>
              <Button asChild variant="hero" size="lg">
                <Link to={localePath("/start-a-project")}>{t("Start a Project", "ابدأ مشروعك")}</Link>
              </Button>
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="py-12 lg:py-32 bg-secondary/30">
            <div className="container px-6">
              <h2 className="text-2xl font-display font-bold mb-8">{t("Related Projects", "مشاريع ذات صلة")}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={localePath(`/work/${r.slug}`)}
                    className="group rounded-2xl bg-card border border-border hover:border-primary/30 transition-all duration-300 shadow-card hover:shadow-glow overflow-hidden"
                  >
                    {r.featured_image && (
                      <div className="aspect-[16/10] overflow-hidden bg-secondary">
                        <img src={resolveAssetUrl(r.featured_image)} alt={t(r.title, r.title_ar)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-sans font-medium text-sm group-hover:text-primary transition-colors">{t(r.title, r.title_ar)}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
